import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Heart, Sparkles, Eye, EyeOff } from 'lucide-react';
import { SEOHelmet, SEO_CONFIG } from '@/components/SEOHelmet';
import Footer from '@/components/Footer';
import InstallPWA from '@/components/InstallPWA';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function Auth({ onLogin }) {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    partner_name: '',
    relationship_start_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = isLogin ? 'Giriş Yap - Remindora' : 'Kayıt Ol - Remindora';
    
    // Google Ads Conversion Tracking - Page View
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: isLogin ? 'Login Page' : 'Registration Page',
        page_location: window.location.href,
        page_path: '/auth'
      });
    }
  }, [isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get reCAPTCHA token - use bypass if fails
    let recaptchaToken = 'mobile_app_bypass';
    if (recaptchaRef.current) {
      const token = recaptchaRef.current.getValue();
      if (token) {
        recaptchaToken = token;
      }
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password, recaptcha_token: recaptchaToken }
        : { ...formData, recaptcha_token: recaptchaToken };

      const response = await axios.post(`${API}${endpoint}`, payload);
      const { access_token, user } = response.data;

      toast.success(isLogin ? 'Giriş başarılı!' : 'Hesap oluşturuldu!');
      
      // Mark as new user if registering
      if (!isLogin) {
        localStorage.setItem('is_new_user', 'true');
        
        // Google Ads Conversion Tracking - Registration Success
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-XXXXXXXXXX/XXXXXXXXXXXXX',
            'event_callback': function() {
              console.log('Conversion tracked: User Registration');
            }
          });
          
          // Track as sign_up event for GA4
          window.gtag('event', 'sign_up', {
            method: 'email',
            user_id: user.id
          });
        }
      } else {
        // Track login event
        if (window.gtag) {
          window.gtag('event', 'login', {
            method: 'email'
          });
        }
      }
      
      onLogin(access_token, user);
    } catch (error) {
      let message = 'Bir hata oluştu';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (validation errors)
        if (Array.isArray(detail)) {
          message = detail.map(err => err.msg || err.message || 'Geçersiz değer').join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          message = detail;
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          message = detail.msg || detail.message || 'Geçersiz giriş';
        }
      }
      
      toast.error(message);
      
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHelmet {...SEO_CONFIG.auth} />
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafafa 0%, #dbeafe 50%, #fafafa 100%)',
        padding: '2rem 1rem',
        width: '100%'
      }}>
        <div style={{ maxWidth: '28rem', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <img src="/logo-auth.png" alt="Remindora - Yıldönümü Hatırlatıcı" style={{ height: '12rem', width: 'auto', objectFit: 'contain', margin: '0 auto 0.75rem auto', display: 'block' }} />
            <p style={{ color: '#525252', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <Sparkles className="w-4 h-4" />
              Sevdiklerinizle özel anları hatırlayın
            </p>
          </div>

        {/* Auth Card */}
        <Card className="glass border-neutral-200 shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{isLogin ? 'Giriş Yap' : 'Hesap Oluştur'}</CardTitle>
            <CardDescription>
              {isLogin
                ? 'Özel günlerinize erişin'
                : 'Sevdiklerinizle özel günleri takip edin'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2 text-center">
                <Label htmlFor="email" data-testid="email-label" className="block">E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ornek@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                  className="border-neutral-300"
                />
              </div>

              <div className="space-y-2 text-center">
                <Label htmlFor="password" data-testid="password-label" className="block">Şifre</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="password-input"
                    className="border-neutral-300 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <>
                  <div className="space-y-2 text-center">
                    <Label htmlFor="partner_name" data-testid="partner-name-label" className="block">Partner Adı</Label>
                    <Input
                      id="partner_name"
                      type="text"
                      placeholder="Sevgilinizin adı"
                      value={formData.partner_name}
                      onChange={(e) => setFormData({ ...formData, partner_name: e.target.value })}
                      required={!isLogin}
                      data-testid="partner-name-input"
                      className="border-neutral-300"
                    />
                  </div>
                  
                  <div className="space-y-2 text-center">
                    <Label htmlFor="relationship_start_date" className="block">
                      İlişki Başlangıç Tarihi <span className="text-xs text-neutral-500">(Opsiyonel)</span>
                    </Label>
                    <Input
                      id="relationship_start_date"
                      type="date"
                      value={formData.relationship_start_date}
                      onChange={(e) => setFormData({ ...formData, relationship_start_date: e.target.value })}
                      className="border-neutral-300"
                      max={new Date().toISOString().split('T')[0]}
                    />
                    <p className="text-xs text-neutral-500">
                      İlk tanışma tarihini girin, otomatik olarak yıldönümünüz eklenecek 💝
                    </p>
                  </div>
                </>
              )}

              {isLogin && (
                <div className="text-right mb-2">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-purple-600 hover:underline"
                  >
                    Şifremi Unuttum
                  </button>
                </div>
              )}

              {/* reCAPTCHA */}
              <div className="flex justify-center my-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? 'Lütfen bekleyin...' : isLogin ? 'Giriş Yap' : 'Hesap Oluştur'}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                  }
                }}
                className="text-sm text-blue-600 hover:underline"
                data-testid="toggle-auth-button"
              >
                {isLogin ? 'Hesabınız yok mu? Kayıt olun' : 'Zaten hesabınız var mı? Giriş yapın'}
              </button>
              
              {/* Mobile Install Button - Below toggle link */}
              <div className="mt-4 md:hidden flex justify-center">
                <InstallPWA />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
    </>
  );
}
